// Every device of the signed-in user (design/isomorphic/src/pages/devices.tsx): its kind, its auth key's
// fingerprint, whether it holds the account key or the signed list names it, when it was last
// seen, and its state.
import { Keyhole } from "@phosphor-icons/react";

import type { Device } from "../api/client";
import { type Messages, useT } from "../i18n";
import { ago, day } from "../pages/format";
import { base64ToBytes } from "../proto/bytes";
import type { SigAlg } from "../proto/frames";
import type { Proto } from "../proto/index";
import { type Column, DataTable } from "../ui/DataTable";
import { DotStatus, Mono, Pill, type Tone } from "../ui/status";
import { DeviceIcon } from "./DeviceIcon";
import { kindLabel } from "./kinds";
import { deviceFingerprint } from "./fingerprint";

const STATE: Record<string, Tone> = { active: "green", pending: "orange", revoked: "red" };

export interface DeviceMarks {
  proto?: Proto;
  /** This browser's device id, when it is linked or pending. */
  self?: string;
  /** Whether this browser holds the account key. */
  selfHolds: boolean;
  /** The ids the verified recipient list names. */
  recipients?: string[];
}

/** The listed key's fingerprint, or undefined when the API's key does not parse. */
function fingerprintOf(proto: Proto, d: Device): string | undefined {
  try {
    return deviceFingerprint(proto, d.auth_alg as SigAlg, base64ToBytes(d.auth_public_key));
  } catch {
    return undefined;
  }
}

function columns(t: Messages["devices"], { proto, self, selfHolds, recipients }: DeviceMarks): Column<Device>[] {
  const states: Partial<Record<string, string>> = t.states;
  return [
    {
      key: "name",
      header: t.table.device,
      cell: (d) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
            <DeviceIcon kind={d.kind} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 font-semibold text-gray-900">
              <span className="truncate">{d.name}</span>
              {d.id === self ? (
                <Pill tone="primary" className="px-1.5 py-0 text-[10.5px]">
                  {t.table.thisBrowser}
                </Pill>
              ) : null}
            </div>
            <div className="text-xs text-gray-500">
              {t.table.added(kindLabel(d.kind), day(d.created_at))}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "fp",
      header: t.table.fingerprint,
      hideBelow: "lg",
      cell: (d) => <Mono>{(proto && fingerprintOf(proto, d)) ?? "—"}</Mono>,
    },
    {
      key: "key",
      header: t.table.accountKey,
      hideBelow: "md",
      cell: (d) => {
        if (d.id === self && selfHolds) return <Pill tone="secondary" icon={<Keyhole weight="duotone" />}>{t.table.holdsIt}</Pill>;
        if (recipients?.includes(d.id)) return <Pill tone="gray">{t.table.inSignedList}</Pill>;
        return <span className="text-gray-400">—</span>;
      },
    },
    { key: "seen", header: t.table.lastSeen, hideBelow: "sm", cell: (d) => (d.last_seen_at ? ago(d.last_seen_at) : "—") },
    {
      key: "status",
      header: t.table.state,
      cell: (d) => <DotStatus tone={STATE[d.status] ?? "gray"} label={states[d.status] ?? d.status} />,
    },
  ];
}

export function DevicesTable({ devices, marks }: { devices: Device[]; marks: DeviceMarks }) {
  const t = useT().devices;
  return <DataTable label={t.page.all} columns={columns(t, marks)} rows={devices} rowKey={(d) => d.id} />;
}
